import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
	createPaymentVirtualAccount,
	disburseSettlement,
	fetchSettlementDetail,
	fetchSettlementList,
} from '../services/api'
import { useToken } from '../utils/auth'

/**
 * 정산 목록 조회 훅
 * @returns {Object} { data, isLoading, isError, error, refetch }
 */
export const useSettlements = () => {
	const token = useToken()

	return useQuery({
		queryKey: ['settlements'],
		queryFn: () => fetchSettlementList(token),
		enabled: !!token,
		retry: 1,
	})
}

/**
 * 정산 상세 조회 훅
 * @param {string} contractPaymentId - 조회할 정산 ID
 * @returns {Object} { data, isLoading, isError, error, refetch }
 */
export const useSettlementDetail = (contractPaymentId) => {
	const token = useToken()

	return useQuery({
		queryKey: ['settlement-detail', contractPaymentId],
		queryFn: () => fetchSettlementDetail(token, contractPaymentId),
		enabled: !!token && !!contractPaymentId,
		retry: 1,
	})
}

/**
 * 정산 가상 계좌 생성 훅
 * @returns {Object} { mutate, mutateAsync, isLoading, isError, ... }
 */
export const useCreateSettlementVirtualAccount = () => {
	const token = useToken()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (payload) => {
			if (!token) {
				throw new Error('인증 토큰이 없습니다.')
			}
			return createPaymentVirtualAccount(token, payload)
		},
		onSuccess: (_, variables) => {
			// queryClient.invalidateQueries({ queryKey: ['settlements'] })

			const contractPaymentId = variables?.contractPaymentId ?? variables?.id ?? null

			if (contractPaymentId) {
				queryClient.invalidateQueries({
					queryKey: ['settlement-detail', contractPaymentId],
				})
			}
		},
	})
}

/**
 * 정산 지급 요청 훅
 * @returns {Object} mutation helpers
 */
export const useDisburseSettlement = () => {
	const token = useToken()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async ({ contractPaymentId }) => {
			if (!token) {
				throw new Error('인증 토큰이 없습니다.')
			}
			return disburseSettlement(token, contractPaymentId)
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['settlements'] })
			if (variables?.contractPaymentId) {
				queryClient.invalidateQueries({
					queryKey: ['settlement-detail', variables.contractPaymentId],
				})
			}
		},
	})
}
